import React, { useState, useEffect, useCallback, useMemo } from "react";
import AdminLayout from "./AdminLayout";
import AdminWorkstation from "./AdminWorkstation";
import AdminNotification from "./AdminNotification";
import AdminSummary from "./AdminSummary";
import AdminTracker from "./AdminTracker";
import AdminStock from "./AdminStock";
import AdminReport from "./AdminReport";
import AdminProduct from "./AdminProduct";
import AdminProgress from "./AdminProgress";
import AdminReceive from "./AdminReceive";
import AdminTreat from "./AdminTreat";
import AdminPaint from "./AdminPaint";
import AdminPack from "./AdminPack";
import AdminDeliver from "./AdminDeliver";
import AdminData from "./AdminData";
import AdminStaff from "./AdminStaff";
import AdminProfile from "./AdminProfile";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import { getDatabase, ref, onValue, get } from "firebase/database";
import "./AdminDashboard.css"; 

// 🚀 FIX 1: Move static component map out of the component to strip it as a dependency
const NODE_COMPONENTS = { 
  Receive: AdminReceive, 
  Treat: AdminTreat, 
  Paint: AdminPaint, 
  Pack: AdminPack, 
  Deliver: AdminDeliver 
};

const LiveClock = () => {
  const [time, setTime] = useState("");
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setTime(now.toLocaleString("en-MY", {
        weekday: "short", day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
      }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  return <p className="live-clock">{time || "Loading..."}</p>;
};

// Extracted Notification Hub
const NotificationHub = ({ notifications, showOverlay, toggleOverlay, setShowOverlay, navigateTo }) => {
  const pendingAlerts = useMemo(() => notifications.filter(alert => alert.status === "Pending"), [notifications]);

  const groupedAlerts = useMemo(() => {
    return pendingAlerts.reduce((groups, alert) => {
      const date = alert.dateTime ? alert.dateTime.split(" ")[0] : "Unknown Date";
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(alert);
      return groups;
    }, {});
  }, [pendingAlerts]);

  const sortedDates = useMemo(() => {
    return Object.keys(groupedAlerts).sort((a, b) => {
      const [ad, am, ya] = a.split("/");
      const [bd, bm, by] = b.split("/"); 
      return new Date(by, bm - 1, bd) - new Date(ya, am - 1, ad);
    });
  }, [groupedAlerts]);

  return (
    <div className="notification-wrapper">
      <button className="btn-notification" onClick={toggleOverlay}>
        <div className="btn-notif-content">
          <span>Notification</span>
          <div className="notif-badge-container">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            {pendingAlerts.length > 0 && <span className="notif-count-dot">{pendingAlerts.length}</span>}
          </div>
        </div>
      </button>

      {showOverlay && (
        <div className="notification-overlay">
          <div className="overlay-header">
            <span>System Alerts ({pendingAlerts.length})</span>
            <button onClick={() => setShowOverlay(false)} className="close-mini">×</button>
          </div>
          
          <div className="notification-list-scrollable">
            {pendingAlerts.length > 0 ? (
              sortedDates.map(date => (
                <div key={date} className="date-group">
                  <div className="notification-date-header">{date}</div>
                  
                  {groupedAlerts[date].map((alert) => (
                    <div 
                      key={alert.id} 
                      className="notification-item alert-type-item"
                      onClick={() => navigateTo("notification", alert.fgNumber)}
                    >
                      <div className="note-content">
                        <div className="note-header-row">
                          <p className="note-title">{alert.type}</p>
                          <span className="note-station-tag">{alert.station}</span>
                        </div>
                        
                        <p className="note-fg">FG: <strong>{alert.fgNumber}</strong></p>
                        <p className="note-msg">{alert.message}</p>
                        
                        <div className="note-footer">
                          <span className="note-time">{alert.dateTime ? alert.dateTime.split(" ")[1] : ""}</span>
                          <span className="note-status-badge">{alert.status}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <p className="empty-notes" style={{ textAlign: "center", padding: "20px", color: "#64748b" }}>No pending alerts</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function AdminPages({ onProfileClick, onNavigateToProgress, sharedSelectedFg, setSharedSelectedFg }) {
  const [currentPage, setCurrentPage] = useState("home");
  const [selectedFg, setSelectedFg] = useState(null);

  // Persistence States
  const [globalProgressData, setGlobalProgressData] = useState([]);
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);
  const [untreatedFGs, setUntreatedFGs] = useState([]);
  const [delayedFGs, setDelayedFGs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [wipView, setWipView] = useState("minimized");
  const [delayedView, setDelayedView] = useState("minimized");
  const [showOverlay, setShowOverlay] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [progressIssuesList, setProgressIssuesList] = useState([]);
  const [showProgressOverlay, setShowProgressOverlay] = useState(false);
  const [filterContext, setFilterContext] = useState("");

  const toggleOverlay = () => {
    setShowOverlay(prev => !prev);
  };

  // Helpers
  const saveToCache = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), payload: data }));
    } catch (e) { console.error("Cache Save Error", e); }
  };

  const getFromCache = (key) => {
    try {
      const cached = localStorage.getItem(key);
      return cached ? JSON.parse(cached).payload : null;
    } catch (e) { return null; }
  };

  const parseDate = useCallback((str) => {
    if (!str) return new Date();
    const [d, m, y] = str.split("/");
    return new Date(`${y}-${m}-${d}`);
  }, []);

  const getWorkingDays = useCallback((startDateStr) => {
    if (!startDateStr) return 0;
    let date = parseDate(startDateStr);
    const today = new Date();
    let count = 0;
    let current = new Date(date.getTime());
    while (current <= today) {
      if (current.getDay() !== 0) count++;
      current.setDate(current.getDate() + 1);
    }
    return count;
  }, [parseDate]);

  // Updated navigation engine to dynamically flush parameters
  const navigateTo = useCallback((key, fg = null) => {
    setCurrentPage(key);
    setSelectedFg(null);
    
    if (setSharedSelectedFg) {
      setSharedSelectedFg(fg); 
    }

    if (fg && typeof fg === "string" && fg.includes(" — ")) {
      setFilterContext(fg);
    } else if (!fg) {
      setFilterContext(""); 
    }
    
    setShowOverlay(false); 
    const url = fg ? `?page=${key}&fg=${fg}` : `?page=${key}`;
    window.history.pushState({ page: key, fg: fg }, "", url);
  }, [setSharedSelectedFg]);

  useEffect(() => {
    const cachedUntreated = getFromCache("admin_untreated_cache");
    const cachedDelayed = getFromCache("admin_delayed_cache");
    if (cachedUntreated) setUntreatedFGs(cachedUntreated);
    if (cachedDelayed) setDelayedFGs(cachedDelayed);
  }, []);

  // Dynamically captures URL changes on back navigation transitions
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pageFromUrl = params.get("page");
    const fgFromUrl = params.get("fg");
    
    if (pageFromUrl) {
      setCurrentPage(pageFromUrl);
      if (fgFromUrl && typeof setSharedSelectedFg === "function") {
        setSharedSelectedFg(fgFromUrl);
        if (fgFromUrl.includes(" — ")) {
          setFilterContext(fgFromUrl);
        }
      }
    } else {
      window.history.replaceState({ page: "home" }, "", "?page=home");
    }

    const handlePopState = (event) => {
      if (event.state && event.state.page) {
        setCurrentPage(event.state.page);
        if (typeof setSharedSelectedFg === "function") {
          setSharedSelectedFg(event.state.fg || null);
          if (event.state.fg && event.state.fg.includes(" — ")) {
            setFilterContext(event.state.fg);
          } else {
            setFilterContext("");
          }
        }
        setSelectedFg(null);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [setSharedSelectedFg]);

  const startLiveSync = useCallback(() => {
    setIsLoading(true);
    const db = getDatabase();
    const progressRef = ref(db, "Progress");

    get(progressRef).then((snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const untreated = [];
        const delayed = [];

        Object.entries(data).forEach(([fgNumber, child]) => {
          const rDate = child.receivingDate || "";
          if (!rDate || rDate.trim() === "") return;

          const hasS1 = child.s1 === true || child.hasOwnProperty('s1');
          const hasS2 = child.s2 === true || child.hasOwnProperty('s2');
          const hasS5 = child.s5 === true || child.hasOwnProperty('s5');
          const hasS4 = child.s4 === true || child.hasOwnProperty('s4');
          const hasS3 = child.s3 === true || child.hasOwnProperty('s3');

          const qtyRaw = child.quantity;
          const quantity = (qtyRaw === null || qtyRaw === undefined || qtyRaw === "") ? "0" : String(qtyRaw);
          
          const baseItem = {
            fgNumber: fgNumber,
            receivingDate: rDate,
            receivingTime: child.receivingTime || "",
            labelType: child.labelType || "",
            quantity: quantity,
          };

          if (hasS1 && !hasS2) {
            untreated.push({ ...baseItem, locationTag: "Receive" });
          }

          if (hasS2 && !hasS5) {
            const workDays = getWorkingDays(rDate);
            let locationTag = "Treat";
            if (hasS4) locationTag = "Pack";
            else if (hasS3) locationTag = "Paint";
            delayed.push({ ...baseItem, workDays, locationTag });
          }
        });

        const sortedUntreated = untreated.sort((a, b) => parseDate(b.receivingDate) - parseDate(a.receivingDate));
        const sortedDelayed = delayed.sort((a, b) => b.workDays - a.workDays);

        setUntreatedFGs(sortedUntreated);
        setDelayedFGs(sortedDelayed);
        saveToCache("admin_untreated_cache", sortedUntreated);
        saveToCache("admin_delayed_cache", sortedDelayed);
      }
      setIsLoading(false);
    }).catch((err) => {
      console.error("Firebase fetch error: ", err);
      setIsLoading(false);
    });

    return () => {}; 
  }, [getWorkingDays, parseDate]);

  useEffect(() => {
    let unsubscribe;
    if (currentPage === "home") {
      unsubscribe = startLiveSync();
    }
    return () => { if (unsubscribe) unsubscribe(); };
  }, [currentPage, startLiveSync]);

  const handleLogout = async () => {
    try { 
      localStorage.removeItem("admin_untreated_cache");
      localStorage.removeItem("admin_delayed_cache");
      await signOut(auth); 
      window.location.reload(); 
    } catch (error) { console.error("Logout failed:", error); }
  };

  const menuItems = [
    { label: "Home", icon: "home_admin.png", key: "home" },
    { label: "Notification", icon: "notification_admin.png", key: "notification"},
    { label: "Workstation", icon: "workstation_admin.png", key: "workstation" },
    { label: "Daily", icon: "summary_admin.png", key: "summary" },
    { label: "Progress", icon: "progress_admin.png", key: "progress" },
    { label: "Tracker", icon: "tracker_admin.png", key: "tracker" },
    { label: "Stock", icon: "stock_admin.png", key: "stock" },
    { label: "Report", icon: "report_admin.png", key: "report" },
    { label: "Data", icon: "data_admin.png", key: "data" },
    { label: "Product", icon: "product_admin.png", key: "product" },
    { label: "Staff", icon: "staff_admin.png", key: "staff" },
    { label: "Profile", icon: "profile_admin.png", key: "profile" },
  ];

  useEffect(() => {
    const db = getDatabase();
    const alertRef = ref(db, "Alerts"); 
    
    const unsubscribe = onValue(alertRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data).map(([id, value]) => ({
          id,
          ...value,
          sortTime: value.timestamp || 0 
        })).sort((a, b) => b.sortTime - a.sortTime);
        
        setNotifications(list);
      } else {
        setNotifications([]);
      }
    });
    return () => unsubscribe();
  }, []);

  // Derived filtered state arrays for side-by-side columns
  const openIssues = useMemo(() => {
    return Array.isArray(progressIssuesList) ? progressIssuesList.filter(i => i.status.toLowerCase() === "open") : [];
  }, [progressIssuesList]);

  const closedIssues = useMemo(() => {
    return Array.isArray(progressIssuesList) ? progressIssuesList.filter(i => i.status.toLowerCase() === "closed") : [];
  }, [progressIssuesList]);

  // 🚀 FIX 2: Added missing dependent hooks into the array context safely
  const memoizedContent = useMemo(() => {
    if (selectedFg && selectedFg.nodeName && NODE_COMPONENTS[selectedFg.nodeName]) {
      const NodeComponent = NODE_COMPONENTS[selectedFg.nodeName];
      return <NodeComponent fgNumber={selectedFg.fgNumber} nodeName={selectedFg.nodeName} onBack={() => setSelectedFg(null)} />;
    }

    switch (currentPage) {
      case "home":
        return (
          <div className="admin-page-container">
            <div className="admin-header-flex">
              <div className="admin-welcome-section">
                <div className="welcome-banner">
                  <h2 className="welcome-text">Welcome, Admin</h2>
                </div>
                <LiveClock />
                <p className="version-tag">System Version 1.8.0</p>
                <NotificationHub 
                  notifications={notifications}
                  showOverlay={showOverlay}
                  toggleOverlay={toggleOverlay}
                  setShowOverlay={setShowOverlay}
                  navigateTo={navigateTo}
                />
              </div>

              <div className="header-right-edge">
                <button onClick={handleLogout} className="btn-logout-modern">
                  <span>Sign Out</span>
                </button>
              </div>
            </div>

            <div className="dashboard-grid">
              <section className={`card-section ${wipView === "maximized" ? "maximized-card" : "minimized-card"}`}>
                <div onClick={() => setWipView(wipView === "minimized" ? "maximized" : "minimized")} className="card-header wip-gradient">
                  <div className="header-info">
                    <span className="card-icon">🚀</span>
                    <span className="card-title">WIP Items ({untreatedFGs.length})</span>
                  </div>
                  <button className="view-toggle-btn">
                    {wipView === "minimized" ? <> <span style={{ marginRight: '6px' }}>⛶</span> Expand </> : <> <span style={{ marginRight: '6px' }}>✕</span> Close </>}
                  </button>
                </div>
                <div className="card-scroll-area">
                  <div className="card-body">
                    {untreatedFGs.map(fg => (
                      <div key={fg.fgNumber} onClick={() => navigateTo("progress", fg.fgNumber)} className="list-item-modern wip-item">
                        <div className="item-main">
                          <strong className="item-id">FG:{fg.fgNumber}</strong>
                          <span className="item-date">Date: {fg.receivingDate}</span>
                        </div>
                        <div className="item-sub-vertical">
                          <span className="item-qty">Qty: {fg.quantity}</span>
                          <span className="item-label">{fg.labelType}</span>
                        </div>
                      </div>
                    ))}                      
                    {!isLoading && untreatedFGs.length === 0 && <div className="empty-state">No pending WIP items</div>}
                  </div>
                </div>
              </section>

              <section className={`card-section ${delayedView === "maximized" ? "maximized-card" : "minimized-card"}`}>
                <div onClick={() => setDelayedView(delayedView === "minimized" ? "maximized" : "minimized")} className="card-header delayed-gradient">
                  <div className="header-info">
                    <span className="card-icon">⚠️</span>
                    <span className="card-title">Delayed Items ({delayedFGs.length})</span>
                  </div>
                  <button className="view-toggle-btn">
                    {delayedView === "minimized" ? <> <span style={{ marginRight: '6px' }}>⛶</span> Expand </> : <> <span style={{ marginRight: '6px' }}>✕</span> Close </>}
                  </button>
                </div>
                <div className="card-scroll-area">
                  <div className="card-body">
                    {delayedFGs.length > 0 ? delayedFGs.map(fg => (
                      <div key={fg.fgNumber} onClick={() => navigateTo("progress", fg.fgNumber)} className="list-item-modern delayed-item">
                        <div className="item-header-row">
                          <strong className="item-id">FG:{fg.fgNumber}</strong>
                          <span className="delay-badge">{fg.workDays} Days Overdue</span>
                        </div>
                        <div className="item-sub-vertical">
                          <span>{fg.locationTag} Stage</span>
                          <span className="item-date">Date: {fg.receivingDate}</span>
                        </div>
                      </div>
                    )) : <div className="empty-state">All shipments on track</div>}
                  </div>
                </div>
              </section>
            </div>
          </div>
        );
      case "notification": 
        return (
          <AdminNotification 
            onBack={() => navigateTo("home")} 
            onNavigateToProgress={(fgNumber) => navigateTo("progress", fgNumber)} 
            targetFg={sharedSelectedFg}
          />
        );
      case "workstation": return <AdminWorkstation onBack={() => navigateTo("home")} />;
      case "summary": {
        let cleanFgNumber = sharedSelectedFg;
        let derivedStation = "";
        let derivedDate = "";

        if (sharedSelectedFg && typeof sharedSelectedFg === "string" && sharedSelectedFg.includes(" — ")) {
          const parts = sharedSelectedFg.split(" — ");
          cleanFgNumber = parts[0]?.trim() || null;
          derivedStation = parts[1]?.trim() || "";
          derivedDate = parts[2]?.trim() || "";
        }

        return (
          <AdminSummary 
            onBack={() => navigateTo("home")} 
            targetFg={cleanFgNumber} 
            initialFilterContext={filterContext}
            targetStation={derivedStation} 
            targetDate={derivedDate}       
            onSelectFgNumber={(fgNumber) => navigateTo("progress", fgNumber)} 
          />
        );
      }
      case "progress":
        return (
          <AdminProgress 
            onBack={() => navigateTo("home")} 
            selectedFG={sharedSelectedFg}        
            setSelectedFG={setSharedSelectedFg}
            cachedData={globalProgressData} 
            setCachedData={setGlobalProgressData} 
            hasLoadedOnce={hasLoadedProgress} 
            setHasLoadedOnce={setHasLoadedProgress} 
            onNavigate={(node, fg) => setSelectedFg({ nodeName: node, fgNumber: fg })} 
            onRegisterAction={setProgressIssuesList}
          />
        );

      case "tracker": return <AdminTracker onBack={() => navigateTo("home")} onNavigate={(view, opts) => navigateTo(view, opts.selectedFG)} />;
      case "stock": return <AdminStock onBack={() => navigateTo("home")} onNavigate={(fg) => navigateTo("progress", fg)} />;
      case "report": 
        return (
          <AdminReport 
            onBack={() => navigateTo("home")} 
            onNavigateToSummary={(fgNumber) => navigateTo("summary", fgNumber)} 
          />
        );
      case "data": return <AdminData onBack={() => navigateTo("home")} />;
      case "product": return <AdminProduct onBack={() => navigateTo("home")} />;
      case "staff": return <AdminStaff onBack={() => navigateTo("home")} />;
      case "profile": return <AdminProfile onBack={() => navigateTo("home")} />;
      default: return <p>Page not found</p>;
    }
  }, [
    currentPage, 
    selectedFg, 
    sharedSelectedFg, 
    untreatedFGs, 
    delayedFGs, 
    isLoading, 
    wipView, 
    delayedView, 
    navigateTo, 
    globalProgressData, 
    hasLoadedProgress, 
    notifications, 
    showOverlay, 
    filterContext, 
    setSharedSelectedFg
  ]);

  return (
    <AdminLayout onProfileClick={onProfileClick} menuItems={menuItems} currentPage={currentPage} onMenuClick={(key) => navigateTo(key)}>
      
      {currentPage !== "home" && currentPage !== "notification" && (
        <div className="global-notification-fixed" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          
      {currentPage === "progress" && !selectedFg && (
        <div className="notification-wrapper" style={{ position: "relative" }}>
          <button 
            className="btn-notification btn-progress-special" 
            onClick={() => setShowProgressOverlay(prev => !prev)}
          >
            <div className="btn-notif-content">
              <span>Station Remark</span>
              <div className="notif-badge-container">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
                {Array.isArray(progressIssuesList) && progressIssuesList.filter(i => i.isUnread).length > 0 && (
                  <span className="notif-count-dot" style={{ backgroundColor: "#ef4444" }}>
                    {progressIssuesList.filter(i => i.isUnread).length}
                  </span>
                )}
              </div>
            </div>
          </button>

          {/* SPLIT OVERLAY POPUP LAYOUT */}
          {showProgressOverlay && (
            <div 
              className="notification-overlay progress-issue-dropdown" 
              style={{ 
                left: "1px", 
                top: "61px", 
                width: "680px", 
                maxWidth: "90vw", 
                display: "flex", 
                flexDirection: "column" 
              }}
            >
              <div className="overlay-header">
                <span>Production Issues ({progressIssuesList?.length || 0})</span>
                <button onClick={() => setShowProgressOverlay(false)} className="close-mini">×</button>
              </div>
              
              <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: "300px" }}>
                
                {/* COLUMN 1: OPEN STATUS */}
                <div style={{ flex: 1, padding: "10px", borderRight: "1px solid #e2e8f0", display: "flex", flexDirection: "column" }}>
                  <p style={{ margin: "0 0 8px 0", fontWeight: "700", fontSize: "0.85rem", color: "#ef4444" }}>
                    Open Alerts ({openIssues.length})
                  </p>
                  
                  <div className="notification-list-scrollable" style={{ flex: 1, overflowY: "auto", maxHeight: "400px" }}>
                    {openIssues.length > 0 ? (
                      openIssues.map((issue) => (
                        <div 
                          key={issue.id} 
                          className="notification-item status-issue-unread"
                          style={{ marginBottom: "8px", borderLeft: "4px solid #ef4444" }}
                          onClick={() => {
                            navigateTo("progress", issue.fgNumber);
                            setShowProgressOverlay(false);
                          }}
                        >
                          <div className="note-content">
                            <div className="note-header-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                              <span className="issue-pill pill-open">OPEN</span>
                              <span className="note-station-tag" style={{ fontSize: "0.75rem", fontWeight: "600" }}>{issue.station} Stage</span>
                            </div>
                            <p className="note-fg" style={{ margin: "2px 0" }}>FG: <strong>{issue.fgNumber}</strong></p>
                            <p className="note-msg" style={{ fontStyle: "italic", color: "#1e293b", margin: "4px 0" }}>"{issue.message}"</p>
                            <div className="note-footer" style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", marginTop: "6px", color: "#64748b" }}>
                              <span>By: {issue.reportedBy}</span>
                              {issue.reportedTime && (
                                <span>{new Date(issue.reportedTime).toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit" })}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p style={{ textAlign: "center", padding: "20px", color: "#94a3b8", fontSize: "0.8rem" }}>No active issues</p>
                    )}
                  </div>
                </div>

                {/* COLUMN 2: CLOSED STATUS */}
                <div style={{ flex: 1, padding: "10px", display: "flex", flexDirection: "column" }}>
                  <p style={{ margin: "0 0 8px 0", fontWeight: "700", fontSize: "0.85rem", color: "#10b981" }}>
                    Closed Logs ({closedIssues.length})
                  </p>
                  
                  <div className="notification-list-scrollable" style={{ flex: 1, overflowY: "auto", maxHeight: "400px" }}>
                    {closedIssues.length > 0 ? (
                      closedIssues.map((issue) => (
                        <div 
                          key={issue.id} 
                          className="notification-item status-issue-read"
                          style={{ marginBottom: "8px", borderLeft: "4px solid #10b981" }}
                          onClick={() => {
                            navigateTo("progress", issue.fgNumber);
                            setShowProgressOverlay(false);
                          }}
                        >
                          <div className="note-content">
                            <div className="note-header-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                              <span className="issue-pill pill-closed">CLOSED</span>
                              <span className="note-station-tag" style={{ fontSize: "0.75rem", fontWeight: "600" }}>{issue.station} Stage</span>
                            </div>
                            <p className="note-fg" style={{ margin: "2px 0" }}>FG: <strong>{issue.fgNumber}</strong></p>
                            <p className="note-msg" style={{ fontStyle: "italic", color: "#475569", margin: "4px 0" }}>"{issue.message}"</p>
                            <div className="note-footer" style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", marginTop: "6px", color: "#64748b" }}>
                              <span>By: {issue.reportedBy}</span>
                              {issue.reportedTime && (
                                <span>{new Date(issue.reportedTime).toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit" })}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p style={{ textAlign: "center", padding: "20px", color: "#94a3b8", fontSize: "0.8rem" }}>No history entries</p>
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>
      )}

          <NotificationHub 
            notifications={notifications}
            showOverlay={showOverlay}
            toggleOverlay={toggleOverlay}
            setShowOverlay={setShowOverlay}
            navigateTo={navigateTo}
          />
        </div>
      )}
      
      {memoizedContent}
    </AdminLayout>
  );
}

export default AdminPages;